import { useKeenSlider } from 'keen-slider/react'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid'
import { tx } from '@twind/core'

import { ArrowProps, DotProps } from './carousel.types'

import 'keen-slider/keen-slider.min.css'

const Arrow = ({ type, disabled, onClick }: ArrowProps) => {
  const ArrowIcon = type === 'left' ? ChevronLeftIcon : ChevronRightIcon

  return (
    <ArrowIcon
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      className={tx(
        'w-12 h-12 text-white absolute top-1/2 -translate-y-1/2 cursor-pointer hover:bg-zinc-900 hover:bg-opacity-20 rounded-full',
        disabled && 'hidden',
        type === 'left' ? 'left-2' : 'right-2'
      )}
    />
  )
}

const Dot = (props: DotProps) => (
  <button
    key={props.index}
    onClick={props.onClick}
    className={
      'w-3 h-3 bg-white rounded-full mx-1 focus:outline-none ' +
      (props.active ? ' bg-opacity-30' : ' bg-opacity-10')
    }
  />
)

const Carousel = (props: {
  children: React.ReactNodeArray
  slidesPerView?: number
  breakpoints?: { breakpoint: number; value: { slidesPerView: number } }[]
  itemClassName?: string
  wrapperClassName?: string
  customArrow?: React.FC<ArrowProps>
  customDot?: React.FC<DotProps>
  hideDots?: boolean
  hideArrow?: boolean
  loopDuration?: number
  loop?: boolean
}) => {
  const [pause, setPause] = useState(false)
  const timer = useRef<number | null>(null)
  const [currentSlide, setCurrentSlide] = useState(0)

  const breakpoints = useMemo(() => {
    if (!props.breakpoints) return {}

    const data: Record<string, {}> = {}
    props.breakpoints.forEach((item) => {
      data[`(min-width: ${item.breakpoint}px)`] = item.value
    })
    return data
  }, [props.breakpoints])

  const [sliderRef, slider] = useKeenSlider({
    initial: 1,
    breakpoints,
    loop: props.loop,
    duration: props.loopDuration,
    centered: true,
    slidesPerView: props.slidesPerView || 1,
    slideChanged: (s) => setCurrentSlide(s.details().relativeSlide),
    dragStart: () => {
      setPause(true)
    },
    dragEnd: () => {
      setPause(false)
    }
  })

  useEffect(() => {
    if (!props.loop && props.loopDuration) return

    timer.current = window.setInterval(() => {
      if (!pause && slider) {
        slider.next()
      }
    }, props.loopDuration)

    return () => {
      if (timer.current) clearInterval(timer.current)
    }
  }, [pause, slider, props.loopDuration, props.loop])

  const ArrowComponent = props.customArrow || Arrow
  const DotComponent = props.customDot || Dot

  return (
    <>
      <div className={`relative ${props.wrapperClassName}`}>
        {/* @ts-ignore */}
        <div ref={sliderRef} className="keen-slider">
          {props.children.flat().map((child, index) => (
            <div
              key={index}
              className={`keen-slider__slide ${props.itemClassName}`}>
              {child}
            </div>
          ))}
        </div>

        {slider && !props.hideArrow && (
          <>
            <ArrowComponent
              type="left"
              onClick={slider.prev}
              disabled={currentSlide === 0}
            />
            <ArrowComponent
              type="right"
              onClick={slider.next}
              disabled={currentSlide === slider.details().size - 1}
            />
          </>
        )}
      </div>

      {slider && !props.hideDots && (
        <div className="w-full flex items-center justify-center mt-6 mb-4">
          {Array.from(Array(slider.details().size).keys()).map((idx, i) => {
            return (
              <DotComponent
                key={i}
                index={idx}
                active={currentSlide === idx}
                onClick={() => slider.moveToSlideRelative(idx)}
              />
            )
          })}
        </div>
      )}
    </>
  )
}

export default Carousel
